/* eslint-env es2020 */
const BASE62_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const SCRAMBLED_ALPHABET = "VPT5atp1FUBCQIhE7ib3gKyqR4evkXwodGDcusYrfm9O2W6nlx8jzMNJLZSAH0";
const SCRAMBLED_MAP = SCRAMBLED_ALPHABET.split("").reduce((map, char, index) => {
  map[char] = index;
  return map;
}, {});

const formatDate = (dateStr) => {
  if (!dateStr) {
    return "N/A";
  }

  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  return `${day}-${months[date.getMonth()]}-${year}`;
};

const encodeBase62 = (num) => {
  let value = BigInt(num);
  if (value === 0n) {
    return "0";
  }

  let result = "";
  while (value > 0n) {
    const rem = Number(value % 62n);
    result = BASE62_ALPHABET[rem] + result;
    value = value / 62n;
  }
  return result;
};

const decodeScrambledBase62 = (str) => {
  let value = 0n;
  for (const char of String(str || "")) {
    const index = SCRAMBLED_MAP[char];
    if (index === undefined) {
      return null;
    }
    value = value * 62n + BigInt(index);
  }
  return value.toString();
};

const isScrambledString = (value) => {
  if (!value) return false;
  return [...String(value)].every((char) => SCRAMBLED_MAP[char] !== undefined);
};

const splitSignatureAndEncodedRsn = (token) => {
  if (!token) {
    return { signature: null, encodedRsn: null, position: null };
  }

  const trimmedToken = String(token).trim();
  if (trimmedToken.length <= 8) {
    return { signature: null, encodedRsn: null, position: null };
  }

  const prefixSig = trimmedToken.slice(0, 8);
  const suffixRsn = trimmedToken.slice(8);
  const suffixSig = trimmedToken.slice(-8);
  const prefixRsn = trimmedToken.slice(0, -8);

  const hasPrefixRsn = isScrambledString(prefixRsn);
  const hasSuffixRsn = isScrambledString(suffixRsn);

  if (hasPrefixRsn) {
    return { signature: suffixSig, encodedRsn: prefixRsn, position: "suffix" };
  }

  if (hasSuffixRsn) {
    return { signature: prefixSig, encodedRsn: suffixRsn, position: "prefix" };
  }

  if (isScrambledString(trimmedToken)) {
    return { signature: null, encodedRsn: trimmedToken, position: "none" };
  }

  return { signature: null, encodedRsn: null, position: null };
};

const computeHmacSignature = async (secretKey, token) => {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secretKey);
  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const digest = await window.crypto.subtle.sign(
    "HMAC",
    cryptoKey,
    encoder.encode(token)
  );

  const digestBytes = new Uint8Array(digest).slice(0, 6);
  const hex = Array.from(digestBytes)
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
  const hashBigInt = BigInt(`0x${hex}`) % (62n ** 8n);
  return encodeBase62(hashBigInt).padStart(8, "0");
};

const verifySignature = async (secretKey, token, signature) => {
  if (!secretKey || !token || !signature) {
    return false;
  }
  const calculated = await computeHmacSignature(secretKey, token);
  return calculated === signature;
};

const decodeTagrosToken = async (token, secretKey) => {
  const result = {
    signature: null,
    encodedRsn: null,
    decodedRsn: null,
    signatureValid: null,
    signatureError: null,
  };

  if (!token) {
    return result;
  }

  const trimmedToken = String(token).trim();
  if (trimmedToken.length === 0) {
    return result;
  }

  const { signature, encodedRsn } = splitSignatureAndEncodedRsn(trimmedToken);
  if (signature && encodedRsn) {
    result.signature = signature;
    result.encodedRsn = encodedRsn;
    const decodedBigInt = decodeScrambledBase62(encodedRsn);
    if (decodedBigInt !== null) {
      const decodedBase62 = encodeBase62(decodedBigInt).padStart(encodedRsn.length, "0");
      result.decodedRsn = decodedBase62;
    }

    if (secretKey) {
      const valid = await verifySignature(secretKey, encodedRsn, signature);
      result.signatureValid = valid;
      if (!valid) {
        result.signatureError = "Signature verification failed.";
      }
    } else {
      result.signatureValid = null;
      result.signatureError = "Signature secret missing.";
    }

    return result;
  }

  if (isScrambledString(trimmedToken)) {
    result.encodedRsn = trimmedToken;
    result.decodedRsn = decodeScrambledBase62(trimmedToken);
    return result;
  }

  return result;
};

export {
  formatDate,
  encodeBase62,
  decodeScrambledBase62,
  isScrambledString,
  splitSignatureAndEncodedRsn,
  computeHmacSignature,
  verifySignature,
  decodeTagrosToken,
};